import { AIHierarchyContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";
import NetworkIcon from "@core/network-icon";
import { IconBox } from "@tabler/icons-react";

// Helper function to adjust color based on level/item index
function adjustHexColorLightness(
  hex: string,
  index: number,
  total: number,
): string {
  if (!hex || typeof index !== "number" || typeof total !== "number") {
    return "#FFFFFF"; // Return white on invalid input
  }

  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Convert hex to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Convert RGB to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  // Calculate lightness adjustment
  const position = index / Math.max(1, total - 1); // 0 to 1
  const adjustment = (position - 0.5) * 0.15; // -0.075 to 0.075
  let newL = l + adjustment;

  // Clamp lightness between 0.1 and 0.9
  newL = Math.max(0.1, Math.min(0.9, newL));

  // Convert HSL back to RGB
  let r1 = 0,
    g1 = 0,
    b1 = 0;
  if (s === 0) {
    r1 = g1 = b1 = newL; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = newL < 0.5 ? newL * (1 + s) : newL + s - newL * s;
    const p = 2 * newL - q;
    r1 = hue2rgb(p, q, h + 1 / 3);
    g1 = hue2rgb(p, q, h);
    b1 = hue2rgb(p, q, h - 1 / 3);
  }

  // Convert RGB to Hex
  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

interface HierarchyTemplateProps {
  content: AIHierarchyContent;
  onUpdate: (newContent: AIHierarchyContent) => void;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const headerVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const levelVariants = {
  hidden: { x: -20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { x: 20, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
};

export function HierarchyTemplate({
  content,
  onUpdate,
}: HierarchyTemplateProps) {
  const [editingLevel, setEditingLevel] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | "level_title" | null
  >(null);
  const [tempValue, setTempValue] = useState<string>("");

  // Handle level title update
  const handleLevelTitleUpdate = (levelIndex: number, newTitle: string) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.levels && newContent.levels[levelIndex]) {
      newContent.levels[levelIndex].level_title = newTitle;
      onUpdate(newContent);
    }
  };

  // Handle item update
  const handleItemUpdate = (
    levelIndex: number,
    itemIndex: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (
      newContent.levels &&
      newContent.levels[levelIndex] &&
      newContent.levels[levelIndex].items &&
      newContent.levels[levelIndex].items[itemIndex]
    ) {
      newContent.levels[levelIndex].items[itemIndex][field] = newText;
      onUpdate(newContent);
    }
  };

  // Ensure levels exist
  if (!content.levels || content.levels.length === 0) {
    return (
      <div className="p-4 text-center text-error">
        Invalid hierarchy data structure.
      </div>
    );
  }

  return (
    <motion.div
      className="p-6 bg-base-200 rounded-lg shadow-md"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Main Title */}
      <motion.h3
        className="text-2xl font-bold mb-8 text-center text-base-content"
        variants={headerVariants}
      >
        <span className="cursor-text">{content.title}</span>
      </motion.h3>

      {/* Hierarchy Levels */}
      <div className="space-y-6">
        {content.levels.map((level, levelIndex) => {
          const levelColor = level.color || "#475569"; // Default slate-600
          const adjustedLevelColor = adjustHexColorLightness(
            levelColor,
            levelIndex,
            content.levels.length,
          );

          return (
            <motion.div
              key={`level-${levelIndex}`}
              className="relative"
              variants={levelVariants}
            >
              {/* Level Title Bar */}
              <div
                className="flex items-center p-3 rounded-t-lg mb-2"
                style={{
                  background: `linear-gradient(to right, ${adjustedLevelColor}, ${
                    content.genericColors[
                      levelIndex % content.genericColors.length
                    ]
                  })`,
                }}
              >
                <div className="flex-shrink-0 mr-3 text-white">
                  {level.iconId ? (
                    <NetworkIcon value={level.iconId} type="LUCIDE" />
                  ) : (
                    <IconBox size={24} />
                  )}
                </div>
                <h4
                  className="text-xl font-semibold text-white focus:outline-none focus:ring-2 focus:ring-primary rounded px-1 flex-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {editingField === "level_title" &&
                  editingLevel === levelIndex ? (
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingField(null);
                        setEditingLevel(null);
                        if (tempValue !== level.level_title) {
                          handleLevelTitleUpdate(levelIndex, tempValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      className="px-1 w-full rounded bg-white bg-opacity-20 outline-none ring-2 ring-primary text-white"
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingField("level_title");
                        setEditingLevel(levelIndex);
                        setTempValue(level.level_title);
                      }}
                      className="cursor-text"
                    >
                      {level.level_title}
                    </span>
                  )}
                </h4>
              </div>

              {/* Connector Line */}
              <div className="absolute left-7 top-12 bottom-0 w-0.5 bg-gray-300 -z-10" />

              {/* Level Items */}
              <div className="pl-14 space-y-4">
                {level.items.slice(0, 4).map((item, itemIndex) => {
                  const itemColor = item.color || adjustedLevelColor;
                  const genericColor =
                    content.genericColors[
                      (levelIndex * 10 + itemIndex) %
                        content.genericColors.length
                    ] || "#E2E8F0";

                  const mixedColor = adjustHexColorLightness(
                    itemColor,
                    itemIndex,
                    level.items.length,
                  );

                  return (
                    <motion.div
                      key={`item-${levelIndex}-${itemIndex}`}
                      className="p-4 rounded-lg shadow-sm relative"
                      style={{
                        background: `linear-gradient(135deg, ${mixedColor}, ${genericColor})`,
                      }}
                      variants={itemVariants}
                      whileHover={{ scale: 1.02, zIndex: 1 }}
                    >
                      {/* Connector to the main line */}
                      {/* Horizontal line to vertical bar */}
                      <div className="absolute left-[-32px] top-5 w-6 h-0.5 bg-gray-300" />
                      {/* Dot on the joint */}
                      <div className="absolute left-[-38px] top-[18px] w-2 h-2 rounded-full bg-gray-400" />

                      <div className="flex gap-3 mb-2">
                        <div className="flex-shrink-0 text-white mt-1">
                          {item.iconId ? (
                            <NetworkIcon value={item.iconId} type="LUCIDE" />
                          ) : (
                            <div
                              className="w-5 h-5 rounded-full"
                              style={{ backgroundColor: mixedColor }}
                            ></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h5
                            className="text-lg font-semibold text-base-content mb-1 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-base-300 bg-opacity-50 rounded px-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {editingField === "title" &&
                            editingLevel === levelIndex &&
                            editingItem === itemIndex ? (
                              <input
                                autoFocus
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                                onBlur={() => {
                                  setEditingField(null);
                                  setEditingLevel(null);
                                  setEditingItem(null);
                                  if (tempValue !== item.title) {
                                    handleItemUpdate(
                                      levelIndex,
                                      itemIndex,
                                      "title",
                                      tempValue,
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.currentTarget.blur();
                                }}
                                className="px-1 w-full rounded bg-base-100 outline-none ring-2 ring-primary"
                              />
                            ) : (
                              <span
                                onClick={() => {
                                  setEditingField("title");
                                  setEditingLevel(levelIndex);
                                  setEditingItem(itemIndex);
                                  setTempValue(item.title);
                                }}
                                className="cursor-text"
                              >
                                {item.title}
                              </span>
                            )}
                          </h5>
                          <div
                            className="text-sm text-base-content focus:outline-none focus:ring-2 focus:ring-primary focus:bg-base-300 bg-opacity-50 rounded px-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {editingField === "description" &&
                            editingLevel === levelIndex &&
                            editingItem === itemIndex ? (
                              <textarea
                                autoFocus
                                rows={3}
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                                onBlur={() => {
                                  setEditingField(null);
                                  setEditingLevel(null);
                                  setEditingItem(null);
                                  if (tempValue !== item.description) {
                                    handleItemUpdate(
                                      levelIndex,
                                      itemIndex,
                                      "description",
                                      tempValue,
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                                className="w-full px-1 rounded bg-base-100 outline-none ring-2 ring-primary"
                              />
                            ) : (
                              <span
                                onClick={() => {
                                  setEditingField("description");
                                  setEditingLevel(levelIndex);
                                  setEditingItem(itemIndex);
                                  setTempValue(item.description);
                                }}
                                className="cursor-text"
                              >
                                {item.description}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
